import { useCallback } from 'react';

import type { GlobalBannerProps } from './GlobalBanner.types';

import { track } from 'apis/amethyst';
import { evGlobalDualBannerClick, evGlobalDualBannerImpression } from 'events/headerFooter';
import { trackEvent } from 'helpers/analytics';
import useEffectOnce from 'hooks/useEffectOnce';
import { cn } from 'helpers/classnames';
import ConditionalLink from 'components/common/ConditionalLink';

const GlobalBanner = ({ banner, slot, testId }: GlobalBannerProps) => {
  useEffectOnce(() => {
    track(() => [
      evGlobalDualBannerImpression,
      {
        creativeId: banner.creativeID,
        slot
      }
    ]);
  });

  const handleBannerClick = useCallback(() => {
    const zfcEventName = slot === 'banner-1' ? 'TE_GLOBAL_DUAL_BANNER_1_CLICK' : 'TE_GLOBAL_DUAL_BANNER_2_CLICK';

    trackEvent(zfcEventName, banner.creativeID);
    track(() => [
      evGlobalDualBannerClick,
      {
        slot,
        creativeId: banner.creativeID
      }
    ]);
  }, [banner.creativeID, slot]);

  // Get dimensions from the desktop source (fallback image)
  const desktopSource = banner.media.primary.sources.find(s => !s.media || s.media === '');
  const width = desktopSource?.width;
  const height = desktopSource?.height;

  const containerBreak = banner.containerBreak ?? false;

  return (
    <ConditionalLink
      href={banner.links[0]?.href}
      onClick={handleBannerClick}
      className={cn('block w-full', {
        'mx-auto max-w-layoutMaxWidth': !containerBreak,
        'max-w-full': containerBreak
      })}
      ariaLabel={banner.media.primary.alt ?? `Global Banner ${slot}`}
      testId={testId ?? 'globalBanner'}
    >
      <picture>
        {banner.media.primary.sources.map((source, index) => (
          <source key={index} {...(source.media && { media: source.media })} srcSet={source.srcset} />
        ))}
        <img src={banner.media.primary.src} alt={banner.media.primary.alt} width={width} height={height} className="block" />
      </picture>
    </ConditionalLink>
  );
};

export default GlobalBanner;
